/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import logo from '../../../../assets/images/wholesale-edn-logo.svg';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import useCurrencyCodeConfiguration from '../../../../hooks/configurations/useCurrencyCodeConfiguration';
import { EdnOrderItem, RetailerEdnOrderModel } from '../../../../app/services/api/orderManagementClient';

export const getPriceInclusiveOfVAT = (price: number, vatRate: number) => {
  const vatRateCalc = vatRate + 100;
  return (price * vatRateCalc) / 100;
};

interface IEdnTemplateProps {
  edn?: RetailerEdnOrderModel;
  clearEdnContents: () => void;
}

type OrderBy = keyof typeof orderByFunction;

const EdnTemplate = ({ edn, clearEdnContents }: IEdnTemplateProps) => {
  const [orderBy] = useState<OrderBy>('description-asc');
  const ednRefContainer = useRef(null);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  const validCurrencyCode = currencyCode && currencyCode.trim() !== '' ? currencyCode : 'GBP';

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: validCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  const printEdn = () => {
    const input = ednRefContainer.current;
    return html2canvas(input as any, { scrollY: window.pageYOffset * -1 }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, '', 'FAST');
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pageHeight;
      }
      pdf.save(`${edn?.FileName}.pdf`);
      clearEdnContents();
    });
  };

  const onNextFrame = (callback: any) => {
    setTimeout(() => requestAnimationFrame(callback), 0);
  };

  const sortOrderItems = (items: EdnOrderItem[]): EdnOrderItem[] => {
    return items.sort((item1: EdnOrderItem, item2: EdnOrderItem): number => orderByFunction[orderBy](item1, item2));
  };

  if (!edn) {
    return null;
  }

  const orderTotal = edn.Items!.reduce(
    (a: any, { QuantityShipped, PriceConfirmedAmount }: any) => {
      const price = PriceConfirmedAmount * 100;
      return a + price * QuantityShipped;
    },
    0,
  );

  const orderTotalWithVat = edn.Items!.reduce(
    (a: any, { QuantityShipped, PriceConfirmedAmount, PriceConfirmedTaxRate }: any) => {
      const price = PriceConfirmedAmount * 100;
      const rowPrice = price * QuantityShipped;
      return a + getPriceInclusiveOfVAT(rowPrice, PriceConfirmedTaxRate);
    },
    0,
  );

  return (
    <Box
      ref={ednRefContainer}
      sx={{
        padding: '1rem 0',
        width: '98%',
        marginLeft: 'auto',
        marginRight: 'auto',
        '@media print': {
          minHeight: '297mm',
        },
      }}
    >
      <Box
        sx={{
          padding: '20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            paddingBottom: '1rem',
          }}
        >
          <Box sx={{ width: '30%' }}>
            <img src={logo}
              aria-label='Morisons Wholesale'
              style={{ maxWidth: '11rem' }}
              onLoad={() => onNextFrame(printEdn)} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              width: '70%',
              flexWrap: 'wrap',
              textTransform: 'capitalize',
            }}
          >
            <Box sx={{ width: '33%', marginBottom: '1rem' }}>
              <Typography fontWeight={600}
                display="block">
                Order Number:
              </Typography>
              <Typography
                display="block">
                {edn?.OrderId || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ width: '33%', marginBottom: '1rem' }}>
              <Typography fontWeight={600}
                display="block">
                PO Number:
              </Typography>
              <Typography
                display="block">
                {edn?.CustomerPONumber ? edn.CustomerPONumber : 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ width: '33%', marginBottom: '1rem' }}>
              <Typography fontWeight={600}
                display="block">
                Location:
              </Typography>
              <Typography
                display="block">
                {edn?.ShipToLocationId || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ width: '33%', marginBottom: '1rem' }}>
              <Typography fontWeight={600}
                display="block">
                Customer:
              </Typography>
              <Typography
                display="block">
                {edn?.CustomerId || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ width: '33%', marginBottom: '1rem' }}>
              <Typography fontWeight={600}
                display="block">
                Order Date:
              </Typography>
              <Typography
                display="block">
                {edn?.OrderRaisedDate || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ width: '33%', marginBottom: '1rem' }}>
              <Typography fontWeight={600}
                display="block">
                Delivery Date:
              </Typography>
              <Typography
                display="block">
                {edn?.ShipToDeliverAt || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ width: '33%', marginBottom: '1rem' }}>
              <Typography fontWeight={600}
                display="block">
                Order Type:
              </Typography>
              <Typography
                display="block">
                {edn?.OrderReferenceCode || 'N/A'}
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                marginBottom: '3rem',
              }}
            >
              <Typography fontWeight={600}
                display="block">
                Delivery Address:
              </Typography>
              <Typography
                display="block">
                <span style={{ paddingRight: '0.25rem' }}>
                  {edn?.ShipToAddressName?.toLowerCase() || 'N/A'},
                </span>
                <span style={{ paddingRight: '0.25rem' }}>
                  {edn?.ShipToAddressLine1?.toLowerCase() || 'N/A'},
                </span>
                <span style={{ paddingRight: '0.25rem' }}>
                  {edn?.ShipToAddressCity?.toLowerCase() || 'N/A'},
                </span>
                <span style={{ paddingRight: '0.25rem' }}>
                  {edn?.ShipToAddressPostCode || 'N/A'}
                </span>
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <Table
              sx={{
                borderLeft: '0.063rem solid #eee',
                borderRight: '0.063rem solid #eee',
              }}
              className="order-items-table"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: 'none',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    PRODUCT NAME
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    PRODUCT ID
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    SINGLE PRICE
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    CASE SIZE
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    UOM
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ORDER QT
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DELIVERED QT
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: '0.063rem solid #eee',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    TOTAL (EX VAT)
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '1rem',
                      borderLeft: '0.063rem solid #eee',
                      borderRight: 'none',
                      borderTop: '0.063rem solid #eee',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    TOTAL (INC VAT)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {edn &&
                  sortOrderItems(edn.Items!).map((item: EdnOrderItem, index: number) => {
                    const singlePrice = item.PriceConfirmedAmount! * 100;
                    const rowPrice = singlePrice * item.QuantityShipped!;

                    return (
                      <TableRow key={index}>
                        <TableCell sx={{ padding: '1rem', borderLeft: 'none', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {item.ItemDescription}
                        </TableCell>
                        <TableCell sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {item.ItemId}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {formatCurrency(singlePrice)}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {item.QuantityType === 'EA' ? '1' : item.ItemCaseSize}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {item.QuantityType}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {edn.Items!.find((x: EdnOrderItem) => x.ItemId === item.ItemId)!.QuantityOrdered}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {item.QuantityShipped}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                          {formatCurrency(rowPrice)}
                        </TableCell>
                        <TableCell align="right"
                          sx={{ padding: '1rem', borderLeft: '0.063rem solid #eee', borderRight: 'none', borderBottom: '0.063rem solid #eee' }}>
                          {formatCurrency(getPriceInclusiveOfVAT(rowPrice, item.PriceConfirmedTaxRate!))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                <TableRow className="tableTotals">
                  <TableCell align="right"
                    colSpan={5}
                    sx={{ padding: '1rem', fontWeight: 'bold', borderLeft: 'none', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                    TOTAL
                  </TableCell>
                  <TableCell align="right"
                    sx={{ padding: '1rem', fontWeight: 'bold', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                    {edn.Items!.reduce((a: any, { QuantityOrdered }: any) => a + QuantityOrdered, 0)}
                  </TableCell>
                  <TableCell align="right"
                    sx={{ padding: '1rem', fontWeight: 'bold', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                    {edn.Items!.reduce((a: any, { QuantityShipped }: any) => a + QuantityShipped, 0)}
                  </TableCell>
                  <TableCell align="right"
                    sx={{ padding: '1rem', fontWeight: 'bold', borderLeft: '0.063rem solid #eee', borderRight: '0.063rem solid #eee', borderBottom: '0.063rem solid #eee' }}>
                    {formatCurrency(orderTotal)}
                  </TableCell>
                  <TableCell align="right"
                    sx={{ padding: '1rem', fontWeight: 'bold', borderLeft: '0.063rem solid #eee', borderRight: 'none', borderBottom: '0.063rem solid #eee' }}>
                    {formatCurrency(orderTotalWithVat)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

const orderByFunction = {
  'description-asc': (item1: EdnOrderItem, item2: EdnOrderItem): number =>
    sortStrings(item1.ItemDescription!, item2.ItemDescription!),
  'description-dsc': (item1: EdnOrderItem, item2: EdnOrderItem): number =>
    sortStrings(item2.ItemDescription!, item1.ItemDescription!),
};

const sortStrings = (s1: string, s2: string): number => {
  const parsed1 = s1.toUpperCase();
  const parsed2 = s2.toUpperCase();

  if (parsed1 < parsed2) {
    return -1;
  }

  if (parsed1 > parsed2) {
    return 1;
  }

  return 0;
};

export default EdnTemplate;